import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import buyerPreferencesService from '../../api/services/buyerPreferences';
import { buyerPreferences, Prisma } from '@prisma/client';
import { fetchBuyerPropertyMatches } from './buyerPropertyMatches';

interface BuyerPreferencesState {
  preferences: buyerPreferences | null;
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
  initialized: boolean; // Track if we've attempted to fetch
}

const initialState: BuyerPreferencesState = {
  preferences: null,
  loading: false,
  error: null,
  lastUpdated: null,
  initialized: false,
};

// Async thunk for fetching buyer preferences
export const fetchBuyerPreferences = createAsyncThunk(
  'buyerPreferences/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const preferences = await buyerPreferencesService.find();
      return preferences;
    } catch (error) {
      return rejectWithValue('Failed to fetch buyer preferences');
    }
  }
);

// Async thunk for saving buyer preferences
export const saveBuyerPreferences = createAsyncThunk(
  'buyerPreferences/save',
  async (preferences: Prisma.buyerPreferencesCreateInput | Prisma.buyerPreferencesUpdateInput, { rejectWithValue }) => {
    try {
      const savedPreferences = await buyerPreferencesService.save(preferences);
      // Backend will automatically trigger property matching and emit socket events
      return savedPreferences;
    } catch (error) {
      return rejectWithValue('Failed to save buyer preferences');
    }
  }
);

// Async thunk for updating buyer preferences
export const updateBuyerPreferences = createAsyncThunk(
  'buyerPreferences/update',
  async ({ id, data }: { id: string; data: Prisma.buyerPreferencesUpdateInput }, { rejectWithValue }) => {
    try {
      const updatedPreferences = await buyerPreferencesService.update(id, data);
      return updatedPreferences;
    } catch (error) {
      return rejectWithValue('Failed to update buyer preferences');
    }
  }
);

const buyerPreferencesSlice = createSlice({
  name: 'buyerPreferences',
  initialState,
  reducers: {
    clearBuyerPreferences: (state) => {
      state.preferences = null;
      state.lastUpdated = null;
      state.error = null;
    },
    setBuyerPreferences: (state, action: PayloadAction<buyerPreferences>) => {
      state.preferences = action.payload;
      state.lastUpdated = new Date().toISOString();
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch preferences
    builder
      .addCase(fetchBuyerPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBuyerPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.preferences = action.payload;
        state.lastUpdated = new Date().toISOString();
        state.initialized = true;
      })
      .addCase(fetchBuyerPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.initialized = true; // Mark as initialized even on error
      });

    // Save preferences
    builder
      .addCase(saveBuyerPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveBuyerPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.preferences = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(saveBuyerPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update preferences
    builder
      .addCase(updateBuyerPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBuyerPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.preferences = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(updateBuyerPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearBuyerPreferences, setBuyerPreferences } = buyerPreferencesSlice.actions;
export default buyerPreferencesSlice.reducer;